import { apiClient } from './api'

// ── Types ──────────────────────────────────────────────────────────────────

export type PortalIssueType = 'bug' | 'feature' | 'question'
export type PortalIssueStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_customer'
  | 'escalated'
  | 'resolved'
  | 'closed'
export type PortalIssuePriority = 'low' | 'medium' | 'high' | 'critical'

export type PortalSpace = {
  id: string
  name: string
  slug: string
  description: string | null
  icon: string
  is_active: boolean
  issue_count: number
  open_issue_count: number
  closed_issue_count: number
  critical_count: number
  high_count: number
  created_at: string
  updated_at: string
}

export type PortalSpaceListResponse = {
  spaces: PortalSpace[]
  total: number
}

export type PortalIssue = {
  id: string
  space_id: string
  space_slug: string | null
  organization_id: string | null
  organization_name: string | null
  organization_type: string | null
  title: string
  description: string
  type: PortalIssueType | string
  status: PortalIssueStatus | string
  priority: PortalIssuePriority | string
  author_user_id: string | null
  author_name: string | null
  created_by_user_id: string | null
  created_by_name: string | null
  assigned_to_user_id: string | null
  assigned_to_name: string | null
  comment_count: number
  github_synced: boolean
  github_html_url: string | null
  github_repo: string | null
  github_issue_number: number | null
  is_internal: boolean
  created_at: string
  updated_at: string
}

export type CommentAttachment = {
  id: string
  file_name: string
  content_type: string
  size_bytes: number
  created_at: string
}

export type PortalIssueComment = {
  id: string
  issue_id: string
  author_user_id: string | null
  author_name: string | null
  content: string
  is_internal: boolean
  github_synced: boolean
  github_author_name: string | null
  github_author_avatar_url: string | null
  attachments: CommentAttachment[]
  created_at: string
}

export type PortalIssueAttachment = {
  id: string
  issue_id: string
  uploaded_by_user_id: string | null
  file_name: string
  content_type: string
  size_bytes: number
  public_url: string
  created_at: string
}

export type PortalIssueCollaborator = {
  id: string
  user_id: string
  user_name: string
  created_at: string
}

export type PortalIssueDetail = {
  comments: PortalIssueComment[]
  attachments: PortalIssueAttachment[]
  collaborators: PortalIssueCollaborator[]
} & PortalIssue

export type PortalIssueListResponse = {
  issues: PortalIssue[]
  total: number
}

export type CreateIssueInput = {
  title: string
  description: string
  type: PortalIssueType
  priority?: PortalIssuePriority
  is_internal?: boolean
  on_behalf_of_user_id?: string
  on_behalf_of_organization_id?: string
}

export type HubUserSearchResult = {
  id: string
  full_name: string
  email: string
  organization_id: string
  organization_name: string | null
  organization_type: string | null
}

export type UpdateIssueInput = {
  title?: string
  description?: string
  status?: PortalIssueStatus
  priority?: PortalIssuePriority
  is_internal?: boolean
}

export type MoveIssueInput = {
  target_space_slug: string
  new_type?: PortalIssueType
  new_priority?: PortalIssuePriority
  reason: string
}

export type ChangePriorityInput = {
  new_priority: PortalIssuePriority
  reason: string
}

export type CreateSpaceInput = {
  name: string
  slug: string
  description?: string
  icon?: string
}

export type UpdateSpaceInput = {
  name?: string
  slug?: string
  description?: string
  icon?: string
  is_active?: boolean
}

export type IssueListParams = {
  type?: PortalIssueType
  status?: PortalIssueStatus
  priority?: PortalIssuePriority
  page?: number
  per_page?: number
}

// ── Admin Input Types ──────────────────────────────────────────────────────

export type CreateServiceStatusInput = {
  name: string
  description?: string
  status?: 'operational' | 'degraded' | 'outage' | 'maintenance'
  display_order?: string
  health_check_type?: HealthCheckType
  health_check_url?: string
  incident_threshold?: number
}

export type UpdateServiceStatusInput = {
  name?: string
  description?: string
  status?: 'operational' | 'degraded' | 'outage' | 'maintenance'
  display_order?: string
  health_check_type?: HealthCheckType
  health_check_url?: string
  incident_threshold?: number
}

export type CreateIncidentInput = {
  title: string
  description?: string
  status?: 'investigating' | 'identified' | 'monitoring' | 'resolved'
  severity?: 'minor' | 'major' | 'critical'
  services?: string
}

export type UpdateIncidentInput = {
  title?: string
  description?: string
  status?: 'investigating' | 'identified' | 'monitoring' | 'resolved'
  severity?: 'minor' | 'major' | 'critical'
  services?: string
}

export type CreateRoadmapItemInput = {
  title: string
  description?: string
  column?: 'planned' | 'in_progress' | 'completed'
  labels?: string
  votes?: string
}

export type UpdateRoadmapItemInput = {
  title?: string
  description?: string
  column?: 'planned' | 'in_progress' | 'completed'
  labels?: string
  votes?: string
}

export type AdminIssueListParams = {
  space_slug?: string
  type?: PortalIssueType
  status?: PortalIssueStatus
  priority?: PortalIssuePriority
  page?: number
  per_page?: number
}

export type SubmitToGithubResponse = {
  message: string
  github_html_url: string
  github_issue_number: number
  github_repo: string
}

// Alias — campos de org já estao em PortalIssue
export type HierarchyIssue = PortalIssue

export type HierarchyIssueListResponse = {
  issues: HierarchyIssue[]
  total: number
}

export type HierarchyIssueListParams = {
  space_slug?: string
  type?: PortalIssueType
  status?: PortalIssueStatus
  priority?: PortalIssuePriority
  search?: string
  page?: number
  per_page?: number
}

// ── Helpers ────────────────────────────────────────────────────────────────

function buildIssueQuery(params?: IssueListParams): string {
  if (!params) return ''
  const parts: string[] = []
  if (params.type) parts.push(`type=${params.type}`)
  if (params.status) parts.push(`status=${params.status}`)
  if (params.priority) parts.push(`priority=${params.priority}`)
  if (params.page) parts.push(`page=${params.page}`)
  if (params.per_page) parts.push(`per_page=${params.per_page}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

function buildAdminIssueQuery(params?: AdminIssueListParams): string {
  if (!params) return ''
  const parts: string[] = []
  if (params.space_slug) parts.push(`space_slug=${params.space_slug}`)
  if (params.type) parts.push(`type=${params.type}`)
  if (params.status) parts.push(`status=${params.status}`)
  if (params.priority) parts.push(`priority=${params.priority}`)
  if (params.page) parts.push(`page=${params.page}`)
  if (params.per_page) parts.push(`per_page=${params.per_page}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

function buildHierarchyIssueQuery(params?: HierarchyIssueListParams): string {
  if (!params) return ''
  const parts: string[] = []
  if (params.space_slug) parts.push(`space_slug=${params.space_slug}`)
  if (params.type) parts.push(`type=${params.type}`)
  if (params.status) parts.push(`status=${params.status}`)
  if (params.priority) parts.push(`priority=${params.priority}`)
  if (params.search) parts.push(`search=${encodeURIComponent(params.search)}`)
  if (params.page) parts.push(`page=${params.page}`)
  if (params.per_page) parts.push(`per_page=${params.per_page}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export function getIssueTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    bug: 'Bug',
    feature: 'Feature',
    question: 'Dúvida',
  }
  return labels[type] || type
}

export function getIssueTypeColor(type: string): string {
  const colors: Record<string, string> = {
    bug: 'red',
    feature: 'blue',
    question: 'yellow',
  }
  return colors[type] || 'gray'
}

export function getIssueStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    open: 'Aberto',
    in_progress: 'Em Andamento',
    waiting_customer: 'Aguardando Cliente',
    escalated: 'Escalonado',
    resolved: 'Resolvido',
    closed: 'Fechado',
  }
  return labels[status] || status
}

export function getIssueStatusColor(status: string): string {
  const colors: Record<string, string> = {
    open: 'blue',
    in_progress: 'indigo',
    waiting_customer: 'yellow',
    escalated: 'orange',
    resolved: 'green',
    closed: 'gray',
  }
  return colors[status] || 'gray'
}

export function getIssuePriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    low: 'Baixa',
    medium: 'Média',
    high: 'Alta',
    critical: 'Crítica',
  }
  return labels[priority] || priority
}

export function getIssuePriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    low: 'gray',
    medium: 'yellow',
    high: 'orange',
    critical: 'red',
  }
  return colors[priority] || 'gray'
}

// ── API ────────────────────────────────────────────────────────────────────

const BASE = '/api/v1/portal-hub'

export type HubConfig = {
  allowed_org_types: string[]
}

export type HubOperator = {
  id: string
  full_name: string
  email: string
}

export type HubAccessRole = {
  id: string
  name: string
  description?: string
}

export type HubAccessMember = {
  id: string
  full_name: string
  email: string
  is_active: boolean
  roles: HubAccessRole[]
}

export type HubAccessResponse = {
  members: HubAccessMember[]
  available_roles: HubAccessRole[]
}

export const portalHubApi = {
  // Config
  getHubConfig: () => apiClient.get<HubConfig>(`${BASE}/config`),

  // Operators (for assignment)
  listOperators: () => apiClient.get<HubOperator[]>(`${BASE}/operators`),

  // User search (for creating issues on behalf of clients)
  searchUsers: (q: string) =>
    apiClient.get<HubUserSearchResult[]>(`${BASE}/users/search?q=${encodeURIComponent(q)}`),

  // Spaces
  listSpaces: () => apiClient.get<PortalSpaceListResponse>(`${BASE}/spaces`),

  getSpace: (slug: string) => apiClient.get<PortalSpace>(`${BASE}/spaces/${slug}`),

  createSpace: (data: CreateSpaceInput) => apiClient.post<PortalSpace>(`${BASE}/spaces`, data),

  updateSpace: (slug: string, data: UpdateSpaceInput) =>
    apiClient.put<PortalSpace>(`${BASE}/spaces/${slug}`, data),

  deleteSpace: (slug: string) => apiClient.delete(`${BASE}/spaces/${slug}`),

  // My Issues
  listMyIssues: (params?: Omit<IssueListParams, 'priority'>) =>
    apiClient.get<PortalIssueListResponse>(`${BASE}/my-issues${buildIssueQuery(params)}`),

  // Issues
  listIssues: (slug: string, params?: IssueListParams) =>
    apiClient.get<PortalIssueListResponse>(
      `${BASE}/spaces/${slug}/issues${buildIssueQuery(params)}`,
    ),

  getIssue: (slug: string, issueId: string) =>
    apiClient.get<PortalIssueDetail>(`${BASE}/spaces/${slug}/issues/${issueId}`),

  createIssue: (slug: string, data: CreateIssueInput, files?: File[]) => {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('description', data.description)
    formData.append('type', data.type)
    formData.append('priority', data.priority || 'medium')
    if (data.on_behalf_of_user_id) {
      formData.append('on_behalf_of_user_id', data.on_behalf_of_user_id)
    }
    if (data.on_behalf_of_organization_id) {
      formData.append('on_behalf_of_organization_id', data.on_behalf_of_organization_id)
    }
    if (data.is_internal) {
      formData.append('is_internal', 'true')
    }
    if (files && files.length > 0) {
      files.forEach((file) => formData.append('attachments', file))
    }
    return apiClient.post<PortalIssue>(`${BASE}/spaces/${slug}/issues`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  updateIssue: (slug: string, issueId: string, data: UpdateIssueInput) =>
    apiClient.put<PortalIssue>(`${BASE}/spaces/${slug}/issues/${issueId}`, data),

  deleteIssue: (slug: string, issueId: string) =>
    apiClient.delete(`${BASE}/spaces/${slug}/issues/${issueId}`),

  // Assignment
  assignIssue: (slug: string, issueId: string, assignedToUserId: string | null) =>
    apiClient.patch<PortalIssue>(`${BASE}/spaces/${slug}/issues/${issueId}/assign`, {
      assigned_to_user_id: assignedToUserId,
    }),

  // Collaborators
  addCollaborator: (slug: string, issueId: string, userId: string) =>
    apiClient.post<PortalIssueCollaborator>(
      `${BASE}/spaces/${slug}/issues/${issueId}/collaborators/${userId}`,
    ),

  removeCollaborator: (slug: string, issueId: string, userId: string) =>
    apiClient.delete(`${BASE}/spaces/${slug}/issues/${issueId}/collaborators/${userId}`),

  // Move
  moveIssue: (slug: string, issueId: string, data: MoveIssueInput) =>
    apiClient.post<PortalIssue>(`${BASE}/spaces/${slug}/issues/${issueId}/move`, data),

  // Change Priority
  changePriority: (slug: string, issueId: string, data: ChangePriorityInput) =>
    apiClient.post<PortalIssue>(`${BASE}/spaces/${slug}/issues/${issueId}/change-priority`, data),

  // Comments
  createComment: (
    slug: string,
    issueId: string,
    content: string,
    isInternal = false,
    files?: File[],
  ) => {
    const formData = new FormData()
    formData.append('content', content)
    formData.append('is_internal', String(isInternal))
    if (files && files.length > 0) {
      files.forEach((file) => formData.append('attachments', file))
    }
    return apiClient.post<PortalIssueComment>(
      `${BASE}/spaces/${slug}/issues/${issueId}/comments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
  },

  deleteComment: (slug: string, issueId: string, commentId: string) =>
    apiClient.delete(`${BASE}/spaces/${slug}/issues/${issueId}/comments/${commentId}`),

  // Attachments
  getAttachmentDownloadUrl: (attachmentId: string) =>
    apiClient.get<{ url: string; file_name: string }>(
      `${BASE}/attachments/${attachmentId}/download`,
    ),

  // Status (public — no auth)
  listServiceStatuses: () => apiClient.get<ServiceStatusListResponse>(`${BASE}/status`),

  listIncidents: () => apiClient.get<IncidentListResponse>(`${BASE}/incidents`),

  // Uptime History (public — no auth)
  getUptimeHistory: (days: number = 90) =>
    apiClient.get<UptimeHistoryResponse>(`${BASE}/status/history?days=${days}`),

  // Roadmap (public read)
  listRoadmapItems: () => apiClient.get<RoadmapItemListResponse>(`${BASE}/roadmap`),

  toggleRoadmapVote: (itemId: string) =>
    apiClient.post<VoteResponse>(`${BASE}/roadmap/${itemId}/vote`),

  // ── Admin CRUD ──────────────────────────────────────────────────────────

  // Service Statuses (admin write)
  createServiceStatus: (data: CreateServiceStatusInput) =>
    apiClient.post<ServiceStatus>(`${BASE}/status`, data),

  updateServiceStatus: (id: string, data: UpdateServiceStatusInput) =>
    apiClient.put<ServiceStatus>(`${BASE}/status/${id}`, data),

  deleteServiceStatus: (id: string) => apiClient.delete(`${BASE}/status/${id}`),

  // Incidents (admin write)
  createIncident: (data: CreateIncidentInput) =>
    apiClient.post<Incident>(`${BASE}/incidents`, data),

  updateIncident: (id: string, data: UpdateIncidentInput) =>
    apiClient.put<Incident>(`${BASE}/incidents/${id}`, data),

  deleteIncident: (id: string) => apiClient.delete(`${BASE}/incidents/${id}`),

  // Roadmap (admin write)
  createRoadmapItem: (data: CreateRoadmapItemInput) =>
    apiClient.post<RoadmapItem>(`${BASE}/roadmap`, data),

  updateRoadmapItem: (id: string, data: UpdateRoadmapItemInput) =>
    apiClient.put<RoadmapItem>(`${BASE}/roadmap/${id}`, data),

  deleteRoadmapItem: (id: string) => apiClient.delete(`${BASE}/roadmap/${id}`),

  // Admin issues (cross-space)
  listAllIssues: (params?: AdminIssueListParams) =>
    apiClient.get<PortalIssueListResponse>(`${BASE}/admin/issues${buildAdminIssueQuery(params)}`),

  // Submit issue to GitHub (admin)
  submitToGithub: (issueId: string, comment?: string) =>
    apiClient.post<SubmitToGithubResponse>(`${BASE}/admin/issues/${issueId}/submit-to-github`, {
      comment: comment || null,
    }),

  // Hierarchy issues (CP/RS/EC)
  listHierarchyIssues: (params?: HierarchyIssueListParams) =>
    apiClient.get<HierarchyIssueListResponse>(
      `${BASE}/hierarchy/issues${buildHierarchyIssueQuery(params)}`,
    ),

  // ── Hub Access Management (system:admin) ──────────────────────────────
  listHubMembers: () => apiClient.get<HubAccessResponse>(`${BASE}/access/members`),

  addHubRole: (userId: string, roleId: string) =>
    apiClient.post(`${BASE}/access/members`, { user_id: userId, role_id: roleId }),

  removeHubRole: (userId: string, roleId: string) =>
    apiClient.delete(`${BASE}/access/members/${userId}/roles/${roleId}`),
}

// ── Status & Roadmap types ────────────────────────────────────────────────

export type HealthCheckType = 'none' | 'http' | 'internal'

export type ServiceStatus = {
  id: string
  name: string
  description: string | null
  status: string
  display_order: string
  health_check_type: HealthCheckType
  health_check_url: string | null
  incident_threshold: number
  failure_count: number
  accelerated_mode: boolean
  created_at: string
  updated_at: string
}

export type ServiceStatusListResponse = {
  services: ServiceStatus[]
  total: number
}

export type Incident = {
  id: string
  title: string
  description: string | null
  status: string
  severity: string
  services: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
}

export type IncidentListResponse = {
  incidents: Incident[]
  total: number
}

export type RoadmapItem = {
  id: string
  title: string
  description: string | null
  column: string
  labels: string | null
  votes: string
  vote_count: number
  user_voted: boolean
  github_node_id: string | null
  github_issue_url: string | null
  github_synced: boolean
  created_at: string
  updated_at: string
}

export type VoteResponse = {
  voted: boolean
  vote_count: number
}

export type RoadmapItemListResponse = {
  items: RoadmapItem[]
  total: number
}

// ── Uptime History types ──────────────────────────────────────────────────

export type DayStatus = {
  date: string
  status: 'operational' | 'degraded' | 'outage' | 'maintenance'
  uptime_pct: number
  total_checks: number
  operational_checks: number
}

export type ServiceUptimeHistory = {
  service_id: string
  service_name: string
  uptime_30d: number
  uptime_90d: number
  days: DayStatus[]
}

export type UptimeHistoryResponse = {
  services: ServiceUptimeHistory[]
  generated_at: string
}

export default portalHubApi
